/**
 * AccountForm — renders the platform's account-definition ui-config as a controlled form (spec §6).
 *
 * Pure field collector, same shape as `PostgresForm` + `useWarehouseFormHandle`: it owns validation
 * and value assembly via an imperative handle, and the parent (drawer/dialog) drives the primary
 * action button. The platform's `component`/`key` shapes are CLOSED (see `accountFormSchema.ts`), so
 * this only ever renders a `textField` or a `selectField` — an unknown `component` degrades to a text
 * input and logs once per definition rather than crashing the form.
 */
import { zodResolver } from '@hookform/resolvers/zod';
import type { AccountFormField } from '@server/rpc-types';
import * as React from 'react';
import { useForm } from 'react-hook-form';

import { Button } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import { Form, FormControl, FormField } from '@/_shared/components/ui/form';
import { Input } from '@/_shared/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/_shared/components/ui/select';
import {
  ACCOUNT_DRAFTS_CLEARED_EVENT,
  readAccountDraft,
  removeAccountDraft,
  saveAccountDraft,
} from '@/_shared/lib/account-drafts';

import { FormSection } from '../warehouseAccount/shared/FormSection';
import { assemblePayload, buildAccountFormSchema, defaultValuesFor, fieldKeyOf } from './accountFormSchema';

export interface AccountFormHandle {
  validate: () => Promise<boolean>;
  clearDraft: () => void;
  getValues: () => { name: string; options: Record<string, unknown> };
  /**
   * The rendered fields exactly as typed — no trimming, no omit rule.
   *
   * For a caller that must not alter a secret (a password or a private key may legitimately begin or
   * end with whitespace) and does its own per-field normalisation. `getValues` above stays the
   * platform-payload shape every account-management path already sends.
   */
  getRawValues: () => Record<string, string>;
  /** Attach a server-side error (e.g. a platform 409 on a duplicate name) to one field, inline. */
  setError: (field: string, message: string) => void;
}

export interface AccountFormProps {
  fields: AccountFormField[];
  draftKey?: string;
  onSubmit?: () => Promise<void>;
  onStartOver?: () => void;
  onEdit?: () => void;
  autoFocus?: boolean;
  pending?: boolean;
  /**
   * The definition's `config.secretFields` — the PLATFORM's own split, and the only set the write path
   * may omit when blank. NOT the ui-config `secret` flag (see the component's own comment).
   */
  secretFields: string[];
  /** Edit mode: prefill, and treat the platform's secret keys as optional + omit-when-blank. */
  account?: { id: string; name: string; options: Record<string, unknown>; secretKeys: string[] };
  definitionName: string;
  /** Called with the raw values on every change, for a caller that renders something from them. */
  onValuesChange?: (values: Record<string, string>) => void;
  /**
   * Create mode: the account name to offer from the other values. It follows them until the admin
   * edits the name field.
   */
  suggestName?: (values: Record<string, string>) => string;
  /** Names already in use. Compared exactly after trimming, like config-backend. */
  takenNames?: ReadonlySet<string>;
  /** The refusal for a name in `takenNames`. */
  takenNameMessage?: string;
  /** Called when a connection field starts or stops showing an error. The account name does not count. */
  onHasErrorsChange?: (hasErrors: boolean) => void;
}

// Logged once per definition so a malformed/unexpected ui-config field is visible in the console
// without spamming it on every re-render.
const warnedDefinitions = new Set<string>();

export const AccountForm = React.forwardRef<AccountFormHandle, AccountFormProps>(function AccountForm(
  {
    fields,
    draftKey,
    onSubmit,
    onStartOver,
    onEdit,
    autoFocus = false,
    pending = false,
    secretFields,
    account,
    definitionName,
    onValuesChange,
    suggestName,
    takenNames,
    takenNameMessage,
    onHasErrorsChange,
  },
  ref,
): React.JSX.Element {
  // WHAT MAY BE OMITTED WHEN BLANK is the definition's `config.secretFields`, never the ui-config
  // `secret` flag and never a union of the two. They are different things from different places:
  // `secretFields` is the platform's own split and literally what it merges per key, while
  // `field.secret` is an optional UI hint for masking. The PATCH contract is that a provided `options`
  // blob REPLACES stored options while secrets inside it merge per key — so omitting a blank field the
  // platform does NOT consider a secret erases its stored value, the same silent-data-loss class as
  // the rename-revert bug. A union keeps that path open, so the split is by question:
  // write path → `secretFields` (here); masking + keep-hint → `field.secret` (in the render below).
  //
  // It is also not `account.secretKeys`: config-backend can swallow a batch secret-read failure and
  // return an empty `secretKeys`, which Lookout cannot tell from an account with no secrets. Treating
  // it as authoritative would mark every secret required, forcing an admin who only wants to rename
  // the account to retype a credential they may not hold (Vamsi H3). The definition never depends on a
  // per-account read, so it degrades correctly either way.
  const secretFieldKeys = React.useMemo(
    () => fields.map(fieldKeyOf).filter((key) => secretFields.includes(key)),
    [fields, secretFields],
  );
  const schema = React.useMemo(
    () =>
      buildAccountFormSchema(fields, {
        definitionName,
        optionalKeys: account ? secretFieldKeys : [],
        secretKeys: secretFields,
        ...(takenNames && takenNameMessage ? { takenNames, takenNameMessage } : {}),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the account id determines create versus edit; stored values do not affect validation
    [fields, account?.id, secretFieldKeys, secretFields, takenNames, takenNameMessage, definitionName],
  );
  const [draft] = React.useState(() => (draftKey ? readAccountDraft(draftKey, fields, secretFields) : null));
  const [restored, setRestored] = React.useState(draft !== null);
  const draftCleared = React.useRef(false);
  const draftsAllowed = React.useRef(true);
  React.useEffect(() => {
    const stopSaving = () => {
      draftsAllowed.current = false;
    };
    window.addEventListener(ACCOUNT_DRAFTS_CLEARED_EVENT, stopSaving);
    return () => window.removeEventListener(ACCOUNT_DRAFTS_CLEARED_EVENT, stopSaving);
  }, []);
  const defaultValues = React.useMemo(
    () => {
      const values = defaultValuesFor(fields, account);
      return suggestName && !account && 'name' in values ? { ...values, name: suggestName(values) } : values;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-time defaults; RHF owns the field values after mount
    [fields, account?.id],
  );
  const formElement = React.useRef<HTMLFormElement>(null);
  const focusField = React.useCallback((key: string) => {
    const control = formElement.current?.elements.namedItem(key);
    if (control instanceof HTMLElement) control.focus();
  }, []);
  const form = useForm<Record<string, string>>({
    resolver: zodResolver(schema),
    mode: 'onTouched',
    defaultValues: { ...defaultValues, ...draft },
  });

  // The suggested name follows the other fields until the admin types in the name field itself.
  const nameEdited = React.useRef(draft !== null);
  /** Put the suggestion in the name field; recheck it when the field shows an error that may be stale. */
  const applySuggestion = React.useCallback(
    (suggest: (values: Record<string, string>) => string) => {
      const next = suggest(form.getValues());
      if (next === form.getValues('name')) return;
      form.setValue('name', next, { shouldValidate: form.getFieldState('name').error !== undefined });
    },
    [form],
  );
  // The taken names can arrive or change after mount: a suggestion that became taken is replaced.
  React.useEffect(() => {
    if (account || nameEdited.current || !suggestName) return;
    applySuggestion(suggestName);
  }, [account, suggestName, applySuggestion]);
  const suggestRef = React.useRef(suggestName);
  React.useEffect(() => {
    suggestRef.current = suggestName;
  }, [suggestName]);
  React.useEffect(() => {
    if (account) return undefined;
    const subscription = form.watch((_values, { name, type }) => {
      if (name === 'name') {
        if (type === 'change') {
          nameEdited.current = true;
          // A clash shows while the admin types, not only after the field loses focus.
          void form.trigger('name');
        }
        return;
      }
      const suggest = suggestRef.current;
      if (nameEdited.current || !suggest) return;
      applySuggestion(suggest);
    });
    return () => {
      subscription.unsubscribe();
    };
  }, [form, account, applySuggestion]);

  React.useEffect(() => {
    if (!onValuesChange) return undefined;
    onValuesChange(form.getValues());
    const subscription = form.watch(() => {
      onValuesChange(form.getValues());
    });
    return () => {
      subscription.unsubscribe();
    };
  }, [form, onValuesChange]);

  React.useEffect(() => {
    const subscription = form.watch((_values, { type }) => {
      if (type !== 'change') return;
      setRestored(false);
      onEdit?.();
      if (draftKey && draftsAllowed.current && !draftCleared.current)
        saveAccountDraft(draftKey, form.getValues(), fields, secretFields);
    });
    return () => subscription.unsubscribe();
  }, [draftKey, form, fields, secretFields, onEdit]);

  React.useEffect(() => {
    if (!autoFocus) return;
    const first = draft
      ? fields.find((field) => field.secret === true || secretFields.includes(fieldKeyOf(field)))
      : fields[0];
    if (first) focusField(fieldKeyOf(first));
  }, [autoFocus, focusField, fields, secretFields, draft]);

  const [serverErrorField, setServerErrorField] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (pending || !serverErrorField) return;
    focusField(serverErrorField);
    setServerErrorField(null);
  }, [pending, serverErrorField, focusField]);

  const startOver = () => {
    draftCleared.current = true;
    if (draftKey) removeAccountDraft(draftKey);
    nameEdited.current = false;
    form.reset(defaultValues);
    setRestored(false);
    onStartOver?.();
    draftCleared.current = false;
    if (fields[0]) focusField(fieldKeyOf(fields[0]));
  };

  const hasErrors = Object.keys(form.formState.errors).some((key) => key !== 'name');
  React.useEffect(() => {
    onHasErrorsChange?.(hasErrors);
  }, [hasErrors, onHasErrorsChange]);
  // An unmounted form shows no error.
  React.useEffect(() => () => onHasErrorsChange?.(false), [onHasErrorsChange]);

  React.useImperativeHandle(
    ref,
    () => ({
      clearDraft: () => {
        draftCleared.current = true;
        if (draftKey) removeAccountDraft(draftKey);
      },
      // Once submitted, the name stops following the suggestion: a retry must send the same name.
      validate: () => {
        setRestored(false);
        nameEdited.current = true;
        return form.trigger(undefined, { shouldFocus: true });
      },
      // In edit mode a blank secret is omitted, so the platform keeps the stored one (spec §D4).
      getValues: () => assemblePayload(fields, form.getValues(), { omitKeys: account ? secretFieldKeys : [] }),
      getRawValues: () => form.getValues(),
      setError: (field, message) => {
        form.setError(field, { type: 'server', message });
        setServerErrorField(field);
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- secretFieldKeys is a fresh array each render; the account id is what actually identifies an identity change
    [form, fields, account?.id, draftKey],
  );

  return (
    <Form {...form}>
      <form
        ref={formElement}
        onSubmit={(e) => {
          e.preventDefault();
          void onSubmit?.();
        }}
        className="grid gap-5"
        noValidate
      >
        {restored && (
          <Callout variant="info" role="status">
            Restored your draft. Enter the password again.
          </Callout>
        )}
        {onSubmit && (
          <Button type="submit" className="sr-only" tabIndex={-1} aria-hidden="true" label="Submit account" />
        )}
        {fields.map((field) => {
          const key = fieldKeyOf(field);
          const isSecret = field.secret === true;
          const isKnownComponent = field.component === 'textField' || field.component === 'selectField';
          if (!isKnownComponent && !warnedDefinitions.has(definitionName)) {
            warnedDefinitions.add(definitionName);
            // Deliberate one-per-definition diagnostic; there is no logger on the SPA side.
            console.warn('rs_account_form_unknown_component', { definitionName, component: field.component });
          }
          const isSelect = field.component === 'selectField';
          // Only for a secret the account ACTUALLY has. A definition can carry an `optional: true`
          // secret the account never held; promising to "keep the current value" there is a lie, and
          // the masked placeholder implies a stored value just as strongly. `secretKeys` is the right
          // source for this one question — it is presentation, not the write path, so its known
          // failure mode (config-backend swallowing a batch secret-read and returning it empty) costs
          // a missing hint, never a lost credential.
          const hasStoredSecret = !!account && isSecret && account.secretKeys.includes(key);
          const secretDescription = hasStoredSecret ? 'Leave blank to keep the current value' : undefined;

          return (
            <FormField
              key={key}
              control={form.control}
              name={key}
              render={({ field: rhf }) =>
                isSelect ? (
                  // omitFormControl: a Radix Select root renders no DOM node, so FormSection's default
                  // FormControl-around-children would strand id/aria-* off any real element — FormControl
                  // has to wrap SelectTrigger instead (see DatabricksForm/RedshiftForm). FormSection still
                  // supplies the card, label, description and message.
                  <FormSection
                    label={field.label}
                    optional={field.optional === true}
                    // Same `?? field.note` fallback the text branch has always had — a select's own
                    // ui-config note was silently dropped (L1/#3).
                    description={secretDescription ?? field.note}
                    omitFormControl
                  >
                    <Select value={rhf.value} onValueChange={rhf.onChange}>
                      <FormControl>
                        <SelectTrigger
                          ref={rhf.ref}
                          name={rhf.name}
                          onBlur={rhf.onBlur}
                          aria-label={field.label}
                          className="w-full"
                        >
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {(field.options ?? []).map((option: { label: string; value: string }) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormSection>
                ) : (
                  <FormSection
                    label={field.label}
                    optional={field.optional === true}
                    description={secretDescription ?? field.note}
                  >
                    <Input
                      {...rhf}
                      onBlur={() => {
                        // The restored blank secret has not been edited. Its first blur must not
                        // move Start over between pointer-down and click.
                        if (draft && isSecret && rhf.value === '' && !form.getFieldState(key).isDirty) return;
                        rhf.onBlur();
                      }}
                      type={isSecret ? 'password' : 'text'}
                      autoComplete="off"
                      placeholder={hasStoredSecret ? '••••••••' : field.placeholder}
                    />
                  </FormSection>
                )
              }
            />
          );
        })}
        {draftKey && (
          <Button
            type="button"
            variant="tertiary"
            className="justify-self-start underline"
            onClick={startOver}
            label="Start over"
          />
        )}
      </form>
    </Form>
  );
});
